import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';

import { Adg4Button } from '@/components/adg4/button/src/button';
import { Picture } from '@/components/wpl/image/src/image';
import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

const ctaButtonStyles = css({
	height: '40px',
	width: '131px',
	whiteSpace: 'nowrap',
});

const dataCenterSectionContainer = css({
	backgroundColor: wacToken('color.primary.blue'),
	clipPath: 'polygon(0% 12.99%, 100% 5%, 100% 100%, 0% 100%)',
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'center',
	gap: '64px',
	padding: '108px 169px 68px',
	[media.below.md]: {
		gap: '22px',
		maxWidth: '640px',
		padding: '108px 54px 64px',
		width: '100%',
	},
	[media.below.sm]: {
		flexDirection: 'column',
	},
});

const headingStyle = css({
	fontSize: '32px',
	lineHeight: '38px',
	margin: '0 0 16px',
	[media.below.md]: {
		fontSize: '28px',
		lineHeight: '34px',
	},
	[media.below.sm]: {
		fontSize: '24px',
		lineHeight: '30px',
	},
});

const descriptionStyle = css({
	fontSize: '16px',
	lineHeight: '24px',
	marginBlockEnd: '24px',
});

const rightSection = css({
	color: wacToken('color.neutral.light0'),
	maxWidth: '760px',
	[media.below.md]: {
		maxWidth: '423px',
	},
});

const ctaWrapper = css({
	alignItems: 'center',
	display: 'flex',
	gap: '16px',
	[media.below.sm]: {
		flexDirection: 'column',
		width: '131px',
	},
});

const svgStyles = css({
	width: '9.0625rem',
	height: '8.875rem',
	[media.below.md]: {
		width: '5.375rem',
		height: '5.25rem',
	},
});

export interface DataCenterSectionProps {
	title?: string;
	description?: string;
	buttonCta?: {
		label?: string;
		href?: string;
	} | null;
	linkCta?: {
		label?: string;
		href?: string;
	} | null;
}

export const DataCenterImage = () => {
	return (
		<Picture
			image={{
				src: `https://wac-cdn.atlassian.com/misc-assets/webp-images/Illo_DataCenter.svg`,
				alt: '',
			}}
			css={[svgStyles]}
		/>
	);
};

export const DataCenterSection = (props: DataCenterSectionProps) => {
	const intl = useIntl();

	const title = intl.formatMessage({
		id: 'Pricing.DataCenterSection.title',
		defaultMessage: 'Data Center Products',
	});
	const description = intl.formatMessage({
		id: 'Pricing.DataCenterSection.description',
		defaultMessage:
			'Data Center is our self-managed product offering for customers with strict regulatory or security requirements. To learn more about this offering, please contact us.',
	});
	const buttonCtaLabel = intl.formatMessage({
		id: 'Pricing.DataCenterSection.buttonCta',
		defaultMessage: 'Contact Us',
	});
	const linkCtaLabel = intl.formatMessage({
		id: 'Pricing.DataCenterSection.linkCta',
		defaultMessage: 'Learn more',
	});

	const buttonCtaHref = `https://www.atlassian.com${useLocalizedHref(
		'/enterprise/contact?formType=product-features',
	)}`;
	const linkCtaHref = `https://www.atlassian.com${useLocalizedHref('/enterprise/data-center')}`;

	return (
		<div css={dataCenterSectionContainer}>
			<DataCenterImage />
			<div css={rightSection}>
				<h2 css={headingStyle} id="data-center-section-heading">
					{props.title ?? title}
				</h2>
				<p css={descriptionStyle}>{props.description ?? description}</p>
				<div css={ctaWrapper}>
					{props.buttonCta !== null && (
						<Adg4Button
							css={ctaButtonStyles}
							size="small"
							variant="secondary"
							color="white"
							colorAgainst="other"
							href={props.buttonCta?.href ?? buttonCtaHref}
						>
							{props.buttonCta?.label ?? buttonCtaLabel}
						</Adg4Button>
					)}
					{props.linkCta !== null && (
						// eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name
						<Link
							bgColor="bgBlackBlue"
							text={props.linkCta?.label ?? linkCtaLabel}
							href={props.linkCta?.href ?? linkCtaHref}
							id="data-center-section-link"
							ariaLabelledby="data-center-section-link data-center-section-heading"
						/>
					)}
				</div>
			</div>
		</div>
	);
};

DataCenterSection.displayName = 'DataCenterSection';
